import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { updateUserProfile } from '../lib/api';
import { ArrowLeft, User, Save } from 'lucide-react';

export default function ProfilePage() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState(user?.name || '');
  const [gender, setGender] = useState(user?.gender || 'male');
  const [playerRole, setPlayerRole] = useState(user?.player_role || 'presse');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateUserProfile({ name, gender, player_role: playerRole });
      await refreshUser();
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      console.error(e);
    }
    setSaving(false);
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <div className="bg-[#121214] border-b-2 border-[#00AEEF]/30 p-4 flex items-center gap-3">
        <button data-testid="back-btn" onClick={() => navigate('/dashboard')} className="text-[#00AEEF] hover:text-[#33C3FF] transition-colors">
          <ArrowLeft size={24} />
        </button>
        <User className="text-[#00AEEF] drop-shadow-[0_0_6px_rgba(0,174,239,0.6)]" size={24} />
        <h1 className="font-heading text-2xl uppercase tracking-wider">Profil</h1>
      </div>

      <div className="max-w-md mx-auto p-4 space-y-6">
        <div className="bg-[#121214] border-2 border-[#00AEEF]/50 shadow-[inset_0_0_20px_rgba(0,174,239,0.15)] p-6">
          <div className="space-y-4">
            <div>
              <label className="block font-mono text-[#00AEEF] text-sm mb-1 uppercase">Email</label>
              <div className="bg-black border-2 border-gray-800 text-[#71717A] font-mono text-lg p-3">
                {user?.email}
              </div>
            </div>
            <div>
              <label className="block font-mono text-[#00AEEF] text-sm mb-1 uppercase">Nom</label>
              <input
                data-testid="profile-name-input"
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                className="bg-black border-2 border-[#00AEEF]/50 text-white font-mono text-lg p-3 w-full focus:border-[#00AEEF] focus:outline-none transition-all rounded-none"
              />
            </div>
            <div>
              <label className="block font-mono text-[#00AEEF] text-sm mb-1 uppercase">Genre (pour la voix IA)</label>
              <div className="flex gap-4">
                <button
                  type="button"
                  data-testid="profile-gender-male"
                  onClick={() => setGender('male')}
                  className={`flex-1 py-3 font-heading text-lg uppercase tracking-wider border-2 transition-all ${
                    gender === 'male'
                      ? 'bg-[#00AEEF] text-black border-[#00AEEF]'
                      : 'bg-transparent text-[#00AEEF] border-[#00AEEF]/30'
                  }`}
                >
                  Homme
                </button>
                <button
                  type="button"
                  data-testid="profile-gender-female"
                  onClick={() => setGender('female')}
                  className={`flex-1 py-3 font-heading text-lg uppercase tracking-wider border-2 transition-all ${
                    gender === 'female'
                      ? 'bg-[#E61C24] text-white border-[#E61C24]'
                      : 'bg-transparent text-[#E61C24] border-[#E61C24]/30'
                  }`}
                >
                  Femme
                </button>
              </div>
            </div>

            <div>
              <label className="block font-mono text-[#00AEEF] text-sm mb-1 uppercase">Rôle</label>
              <div className="flex gap-4">
                <button
                  type="button"
                  data-testid="profile-role-presse"
                  onClick={() => setPlayerRole('presse')}
                  className={`flex-1 py-3 font-heading text-sm uppercase tracking-wider border-2 transition-all ${
                    playerRole === 'presse'
                      ? 'bg-[#00AEEF] text-black border-[#00AEEF]'
                      : 'bg-transparent text-[#00AEEF] border-[#00AEEF]/30'
                  }`}
                >
                  Presse
                </button>
                <button
                  type="button"
                  data-testid="profile-role-commercial"
                  onClick={() => setPlayerRole('commercial')}
                  className={`flex-1 py-3 font-heading text-sm uppercase tracking-wider border-2 transition-all ${
                    playerRole === 'commercial'
                      ? 'bg-[#E61C24] text-white border-[#E61C24]'
                      : 'bg-transparent text-[#E61C24] border-[#E61C24]/30'
                  }`}
                >
                  Commercial
                </button>
              </div>
            </div>

            <button
              data-testid="save-profile-btn"
              onClick={handleSave}
              disabled={saving}
              className="w-full bg-[#E61C24] hover:bg-[#FF333B] text-white font-heading text-lg uppercase tracking-widest py-3 border-b-4 border-red-900 active:border-b-0 active:translate-y-1 transition-all shadow-[0_0_15px_rgba(230,28,36,0.6)] flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <Save size={18} />
              {saving ? 'SAVING...' : saved ? 'SAVED!' : 'SAUVEGARDER'}
            </button>
          </div>
        </div>

        {/* PWA Install Instructions */}
        <div className="bg-[#121214] border-2 border-[#FFF200]/30 p-4">
          <h3 className="font-heading text-sm uppercase tracking-wider text-[#FFF200] mb-3">Installer l'app</h3>
          <div className="space-y-3 font-body text-sm text-[#A1A1AA]">
            <div>
              <p className="text-[#00AEEF] font-heading text-xs uppercase">iPhone (Safari)</p>
              <p>Appuyez sur le bouton Partager &rarr; "Sur l'écran d'accueil"</p>
            </div>
            <div>
              <p className="text-[#39FF14] font-heading text-xs uppercase">Android (Chrome)</p>
              <p>Menu &rarr; "Ajouter à l'écran d'accueil"</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
